import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Chip, CircularProgress, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import SchoolIcon from '@mui/icons-material/School'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SendIcon from '@mui/icons-material/Send'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

const STEP_LABELS: Record<number, string> = {
  1: 'Topic',
  2: 'Question',
  3: 'Background',
  4: 'Hypothesis',
  5: 'Design',
  6: 'Data',
  7: 'Analysis',
  8: 'Report',
}

interface StudentRow {
  student_id:  string
  name:        string
  email:       string
  grade:       number | null
  current_step: number
  class_id:    string | null
  class_name:  string | null
  /** map step_number → score for steps with submissions */
  scores:      Record<number, number | null>
  /** target dates per step */
  targets:     Record<number, string>
  /** submission status of the CURRENT step (steps 1-4): what the mentor sees at a glance */
  subStatus:   'Draft' | 'Submitted' | 'Revise' | null
  last_login:  string | null
}

interface CellState {
  status: 'complete' | 'in_progress' | 'not_started' | 'overdue'
  score:  number | null
  target: string | null
}

function cellState(row: StudentRow, stepNumber: number): CellState {
  const score  = row.scores[stepNumber] ?? null
  const target = row.targets[stepNumber] ?? null
  const overdue = target && stepNumber === row.current_step
    && new Date(target).getTime() < Date.now()
  if (stepNumber < row.current_step) return { status: 'complete',    score, target }
  if (stepNumber === row.current_step) {
    return { status: overdue ? 'overdue' : 'in_progress', score, target }
  }
  return { status: 'not_started', score, target }
}

function cellColor(s: CellState): { bg: string; fg: string } {
  if (s.status === 'complete') {
    if (s.score !== null && s.score >= 85) return { bg: '#1B5E20', fg: '#FFFFFF' }
    if (s.score !== null && s.score >= 70) return { bg: '#388E3C', fg: '#FFFFFF' }
    if (s.score !== null)                  return { bg: '#A5D6A7', fg: '#1A1A2E' }
    return { bg: '#C8E6C9', fg: '#1A1A2E' }   // complete, no score
  }
  if (s.status === 'in_progress') return { bg: '#FFF59D', fg: '#1A1A2E' }
  if (s.status === 'overdue')     return { bg: '#FFCDD2', fg: '#1A1A2E' }
  return { bg: '#F5F5F5', fg: '#9E9E9E' }     // not_started
}

function fmtDate(s: string | null) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function timeSince(iso: string | null) {
  if (!iso) return 'never'
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  if (days < 30) return `${days} days ago`
  return `${Math.floor(days / 30)}mo ago`
}

export default function ProgressGridPage() {
  const { session, role } = useAuth()
  const [rows,    setRows]    = useState<StudentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [resending, setResending] = useState<string | null>(null)
  const [resendMsg, setResendMsg] = useState<string | null>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, role])

  async function load() {
    if (!session) return
    setLoading(true)
    setError(null)

    // Scope (same logic as the Students page):
    //   admin / office_manager      → all enrolled students
    //   instructor / mentor         → their primary mentees (student_mentor
    //                                 lead/active) UNION their class students
    let ids: string[]
    if (role === 'admin' || role === 'office_manager') {
      const { data: allProfiles } = await supabase
        .from('student_profile')
        .select('student_id')
        .in('lifecycle_state', ['enrolled_active', 'enrolled_completed'])
      ids = (allProfiles ?? []).map(p => p.student_id)
    } else {
      const { data: leadAssignments } = await supabase
        .from('student_mentor')
        .select('student_id')
        .eq('mentor_id', session.user.id)
        .eq('mentor_role', 'lead')
        .eq('status', 'active')
      const studentIds = new Set<string>((leadAssignments ?? []).map(r => r.student_id))

      const { data: classMentorRows } = await supabase
        .from('class_mentor')
        .select('class_id')
        .eq('user_id', session.user.id)
      const classIds = (classMentorRows ?? []).map(r => r.class_id)
      if (classIds.length > 0) {
        const { data: enrollments } = await supabase
          .from('class_enrollment')
          .select('student_id')
          .in('class_id', classIds)
          .eq('status', 'active')
        for (const e of enrollments ?? []) studentIds.add(e.student_id)
      }
      ids = Array.from(studentIds)
    }

    if (ids.length === 0) { setRows([]); setLoading(false); return }
    const [{ data: profiles }, { data: projects }, { data: submissions }, { data: targets }] = await Promise.all([
      // Withdrawn students are excluded here too so the mentor branch (scoped
      // by student_mentor / class_enrollment) can't resurface them.
      supabase.from('student_profile').select('student_id, user_id, grade').in('student_id', ids).neq('lifecycle_state', 'withdrawn'),
      supabase.from('project').select('project_id, student_id, current_step, status').in('student_id', ids).eq('status', 'active'),
      // We need score per step. Submissions has step_id (FK to step) — we
      // need step_number. Pull all step rows once and join client-side.
      supabase.from('submission').select('project_id, step_id, score, version_number'),
      supabase.from('project_step_target').select('project_id, step_number, target_date'),
    ])

    const userIds = (profiles ?? []).map(p => p.user_id)
    const { data: users } = await supabase
      .from('users')
      .select('user_id, first_name, last_name, email, last_login')
      .in('user_id', userIds)
    const userMap: Record<string, { first_name: string; last_name: string; email: string; last_login: string | null }> =
      Object.fromEntries((users ?? []).map(u => [u.user_id, u]))

    // step_id → step_number
    const { data: steps } = await supabase.from('step').select('step_id, step_number')
    const stepNumberById: Record<string, number> = Object.fromEntries((steps ?? []).map(s => [s.step_id, s.step_number]))

    // Each student's active class — used to group the grid by class.
    const { data: enrollments } = await supabase
      .from('class_enrollment')
      .select('student_id, class_id')
      .in('student_id', ids)
      .eq('status', 'active')
    const classIdByStudent: Record<string, string> = Object.fromEntries((enrollments ?? []).map(e => [e.student_id, e.class_id]))
    const enrolledClassIds = Array.from(new Set(Object.values(classIdByStudent)))
    let classNameById: Record<string, string> = {}
    if (enrolledClassIds.length > 0) {
      const { data: classRows } = await supabase.from('class').select('class_id, name').in('class_id', enrolledClassIds)
      classNameById = Object.fromEntries((classRows ?? []).map(c => [c.class_id, c.name]))
    }

    const projectByStudent: Record<string, { project_id: string; current_step: number }> =
      Object.fromEntries((projects ?? []).map(p => [p.student_id, { project_id: p.project_id, current_step: p.current_step }]))

    // Submission status of each project's CURRENT step. Steps 1-5 each have a
    // table with status ('draft' | 'submitted' | 'approved_draft' | …) + mentor_note.
    const projIds = (projects ?? []).map(p => p.project_id)
    const [{ data: rtS }, { data: rqS }, { data: brS }, { data: hdS }, { data: edS }] = projIds.length
      ? await Promise.all([
          supabase.from('research_topic').select('project_id, status, mentor_note').in('project_id', projIds),
          supabase.from('research_question').select('project_id, status, mentor_note').in('project_id', projIds),
          supabase.from('background_research').select('project_id, status, mentor_note').in('project_id', projIds),
          supabase.from('hypothesis_design').select('project_id, status, mentor_note').in('project_id', projIds),
          supabase.from('experiment_design').select('project_id, status, mentor_note').in('project_id', projIds),
        ])
      : [{ data: [] }, { data: [] }, { data: [] }, { data: [] }, { data: [] }] as any
    type StepStat = { status: string; mentor_note: string | null }
    const stepStatus: Record<string, Record<number, StepStat>> = {}
    const addStat = (rows: any[] | null, step: number) => {
      for (const r of rows ?? []) (stepStatus[r.project_id] ??= {})[step] = { status: r.status, mentor_note: r.mentor_note }
    }
    addStat(rtS, 1); addStat(rqS, 2); addStat(brS, 3); addStat(hdS, 4); addStat(edS, 5)
    const subStatusFor = (projectId: string, step: number): 'Draft' | 'Submitted' | 'Revise' | null => {
      if (step > 5) return null                       // later steps not built yet
      const st = stepStatus[projectId]?.[step]
      if (!st) return 'Draft'                          // on the step but nothing saved yet
      if (st.status === 'submitted') return 'Submitted'
      if (st.status === 'draft')     return st.mentor_note ? 'Revise' : 'Draft'
      return null                                      // approved/completed → not the current step
    }

    // Latest submission per (project_id, step_id) — submissions are versioned
    const latestSubmission = new Map<string, { score: number | null; version: number }>()
    for (const s of submissions ?? []) {
      const key = `${s.project_id}|${s.step_id}`
      const prev = latestSubmission.get(key)
      if (!prev || (s.version_number ?? 0) > prev.version) {
        latestSubmission.set(key, { score: s.score, version: s.version_number ?? 0 })
      }
    }

    const merged: StudentRow[] = (profiles ?? []).map(p => {
      const u = userMap[p.user_id] ?? { first_name: '', last_name: '', email: '', last_login: null }
      const proj = projectByStudent[p.student_id]
      const scores: Record<number, number | null> = {}
      const targetMap: Record<number, string> = {}
      if (proj) {
        latestSubmission.forEach((val, key) => {
          if (!key.startsWith(proj.project_id + '|')) return
          const stepId = key.split('|')[1]
          const stepN = stepNumberById[stepId]
          if (stepN) scores[stepN] = val.score
        })
        for (const t of targets ?? []) {
          if (t.project_id === proj.project_id) targetMap[t.step_number] = t.target_date
        }
      }
      const classId = classIdByStudent[p.student_id] ?? null
      return {
        student_id:   p.student_id,
        name:         `${u.first_name} ${u.last_name}`.trim() || u.email,
        email:        u.email,
        grade:        p.grade,
        current_step: proj?.current_step ?? 1,
        class_id:     classId,
        class_name:   classId ? (classNameById[classId] ?? null) : null,
        scores,
        targets:      targetMap,
        subStatus:    proj ? subStatusFor(proj.project_id, proj.current_step ?? 1) : null,
        last_login:   u.last_login,
      }
    }).sort((a, b) => a.name.localeCompare(b.name))

    setRows(merged)
    setLoading(false)
  }

  // Group rows by class — named classes first (alphabetical), "No class" last.
  // Rows within a group keep their name sort from load().
  const groupedByClass = useMemo(() => {
    const map = new Map<string, { classId: string | null; className: string; students: StudentRow[] }>()
    for (const r of rows) {
      const key = r.class_id ?? '__none__'
      if (!map.has(key)) {
        map.set(key, { classId: r.class_id, className: r.class_name ?? 'No class', students: [] })
      }
      map.get(key)!.students.push(r)
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.classId === null) return 1
      if (b.classId === null) return -1
      return a.className.localeCompare(b.className)
    })
  }, [rows])

  function viewStudent(r: StudentRow) {
    // Open the student's current step in view-as so staff land on their actual work.
    window.open(`/view-as/${r.student_id}/step/${r.current_step}`, '_blank')
  }

  // Email a set-password / login link (for students who've never accessed the site).
  async function resendInvite(r: StudentRow) {
    setResending(r.student_id); setResendMsg(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('send-student-login-link', {
      body: { student_email: r.email },
    })
    setResending(null)
    if (invokeErr || data?.error || data?.ok === false) {
      setResendMsg(data?.error ? `Could not send to ${r.name}: ${data.error}` : `Could not send to ${r.name}. Please try again.`)
    } else {
      setResendMsg(`Login link sent to ${r.name} (${r.email}).`)
    }
  }

  if (role !== 'admin' && role !== 'office_manager' && role !== 'instructor' && role !== 'mentor') {
    return <Box sx={{ p: 4 }}><Alert severity="warning">Staff only.</Alert></Box>
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1280 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>Student Progress</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        At-a-glance view of where each of your students stands across the 8-step research workflow.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">Legend:</Typography>
        <Chip size="small" label="Complete · ≥85" sx={{ bgcolor: '#1B5E20', color: '#fff' }} />
        <Chip size="small" label="Complete · 70–84" sx={{ bgcolor: '#388E3C', color: '#fff' }} />
        <Chip size="small" label="Complete · <70" sx={{ bgcolor: '#A5D6A7' }} />
        <Chip size="small" label="In progress" sx={{ bgcolor: '#FFF59D' }} />
        <Chip size="small" label="Overdue" sx={{ bgcolor: '#FFCDD2' }} />
        <Chip size="small" label="Not started" sx={{ bgcolor: '#F5F5F5' }} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {resendMsg && (
        <Alert severity={resendMsg.startsWith('Could not') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setResendMsg(null)}>
          {resendMsg}
        </Alert>
      )}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No students assigned to you yet.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { borderRight: '1px solid', borderColor: 'divider' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    Student
                  </TableCell>
                  {Array.from({ length: 8 }, (_, i) => i + 1).map(n => (
                    <TableCell key={n} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', minWidth: 90 }}>
                      <Box>{n}</Box>
                      <Box sx={{ color: 'text.secondary', fontWeight: 400 }}>{STEP_LABELS[n]}</Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 600 }}>Last seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedByClass.map(g => (
                  <React.Fragment key={g.classId ?? '__none__'}>
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        sx={{ bgcolor: 'grey.100', fontWeight: 700, py: 0.75, position: 'sticky', left: 0, borderBottom: '2px solid', borderColor: 'divider' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SchoolIcon fontSize="small" color={g.classId ? 'primary' : 'disabled'} />
                          {g.className}
                          <Typography component="span" variant="caption" color="text.secondary">
                            · {g.students.length} student{g.students.length === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {g.students.map(r => (
                  <TableRow key={r.student_id} hover>
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Grade {r.grade ?? '—'} · Step {r.current_step}
                          </Typography>
                        </Box>
                        <Tooltip title="View student's work">
                          <IconButton size="small" onClick={() => viewStudent(r)} sx={{ mt: -0.25 }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!r.last_login && (
                          <Tooltip title="Email login link (never signed in)">
                            <IconButton
                              size="small" color="warning" sx={{ mt: -0.25 }}
                              onClick={() => resendInvite(r)}
                              disabled={resending === r.student_id}
                            >
                              {resending === r.student_id
                                ? <CircularProgress size={14} />
                                : <SendIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {Array.from({ length: 8 }, (_, i) => i + 1).map(n => {
                      const s = cellState(r, n)
                      const c = cellColor(s)
                      const isCurrent = n === r.current_step
                      const sub = isCurrent ? r.subStatus : null
                      const tooltip = [
                        `Step ${n}: ${STEP_LABELS[n]}`,
                        sub ? `Submission: ${sub}` : `Status: ${s.status.replace('_', ' ')}`,
                        s.score !== null ? `Score: ${s.score}` : null,
                        s.target ? `Target: ${fmtDate(s.target)}` : null,
                      ].filter(Boolean).join(' · ')
                      return (
                        <Tooltip title={tooltip} key={n}>
                          <TableCell
                            align="center"
                            sx={{ bgcolor: c.bg, color: c.fg, fontSize: sub ? '0.68rem' : '0.75rem', fontWeight: 600, p: 1 }}
                          >
                            {s.score !== null ? s.score : (sub ?? '')}
                          </TableCell>
                        </Tooltip>
                      )
                    })}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{timeSince(r.last_login)}</Typography>
                    </TableCell>
                  </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  )
}
